/**
 * Is this stored asset a video?
 *
 * **Video is identified positively, and everything else is an image.** The
 * three galleries used to do the opposite: an allowlist of four image
 * extensions, with `<video>` as the fallback for anything else. That fallback
 * is backwards for two reasons.
 *
 * First, the data. Images outnumber videos by a wide margin, so an unrecognised
 * `type` is far more likely to be an image, and the default should follow the
 * odds rather than fight them.
 *
 * Second, the failure mode. A wrong `<img>` shows a broken-image icon, which
 * reads as "something is wrong here". A wrong `<video>` shows an empty player
 * with working controls, which reads as a real video that happens to be blank.
 * The first invites a bug report; the second gets one only after somebody
 * clicks it and finds the picture underneath. That is exactly how MYD-461 was
 * found: four rows with no `type` at all, rendered as blank players for weeks.
 *
 * The old allowlist also rejected `webp`, `avif` and `heic`, was
 * case-sensitive so `JPG` failed, and rejected MIME-style values so
 * `image/jpeg` failed. All three faults disappear by reversing the question,
 * because none of those are videos.
 */

/**
 * Extensions and MIME subtypes that are genuinely video.
 *
 * `quicktime` is here because `video/quicktime` is what an iPhone sends for a
 * `.mov`, and the subtype does not match the extension.
 */
const VIDEO_TYPES = new Set([
	'mp4',
	'm4v',
	'mov',
	'quicktime',
	'webm',
	'ogv',
	'ogg',
	'avi',
	'mkv',
	'x-matroska',
	'3gp',
	'3gpp',
	'mpeg',
	'mpg',
	'wmv',
	'x-ms-wmv',
	'flv',
	'x-flv',
	'avchd',
	'hevc',
])

/**
 * `type` is stored inconsistently across the image collections: sometimes a
 * bare extension from Cloudinary's `format` ("jpg"), sometimes a full MIME
 * type ("image/jpeg"), and sometimes nothing at all. All three are handled
 * here rather than at each call site.
 *
 * @param type The stored `type`, in any of those shapes, or missing.
 */
export const isVideoType = (type?: string | null): boolean => {
	if (!type) return false

	const normalised = String(type).trim().toLowerCase()
	if (!normalised) return false

	// A full MIME type settles it outright: `video/anything` is a video, and
	// `image/anything` is not, whatever the subtype turns out to be.
	if (normalised.startsWith('video/')) return true
	if (normalised.startsWith('image/')) return false

	// Otherwise it is a bare extension, possibly with a leading dot.
	return VIDEO_TYPES.has(normalised.replace(/^\./, ''))
}

/**
 * The inverse, for call sites that read better in the positive.
 *
 * Note this is NOT "is a known image format": it is "is not a video", which is
 * the question the galleries actually need to answer. A row with no `type` is
 * an image as far as this is concerned, deliberately.
 */
export const isImageType = (type?: string | null): boolean => !isVideoType(type)

export default isVideoType
